import { Toast } from "@appretio/ui-kit";
import type { ReactNode } from "react";
import { createElement, useCallback, useState } from "react";

/** Internal state shape for the toast. */
interface ToastState {
  /** Whether the toast is currently visible. */
  open: boolean;
  /** Short message displayed in the toast. */
  title: string;
}

/** Return type of the {@link useManageCubesOperationToast} hook. */
interface UseManageCubesOperationToastReturn {
  /** Show a success toast with the given message. */
  showSuccess: (title: string) => void;
  /**
   * A pre-rendered `<Toast>` element. Caller MUST render it (e.g. at the
   * top of its returned fragment) — `showSuccess` mutates the state behind
   * it, but the element itself is not placed in the tree by this hook.
   */
  ToastElement: ReactNode;
}

/**
 * Encapsulates the success-toast state that every ManageCubes operation
 * component duplicates. Success only: failures are toasted by the global
 * mutation handler (with the server's reason), so a local error toast
 * would duplicate it.
 */
export function useManageCubesOperationToast(): UseManageCubesOperationToastReturn {
  const [state, setState] = useState<ToastState>({
    open: false,
    title: ""
  });

  const showSuccess = useCallback(function showSuccess(title: string) {
    setState({ open: true, title });
  }, []);

  const ToastElement = createElement(Toast, {
    open: state.open,
    setOpen: function setOpen(open: boolean) {
      setState(function updateOpen(prev) {
        return { ...prev, open };
      });
    },
    variant: "success",
    title: state.title
  });

  return { showSuccess, ToastElement };
}
